import type { AbandonedSelection, SearchProductList } from '@/components/TripActivity/types'
import useSeti from '@/hooks/useSeti'
import analytics from '@/shared-utils/analytics'
import fetchAbandonedSelections from '@/shared-utils/fetchAbandonedSelections'
import { useQuery } from '@tanstack/react-query'
import { getFirstValidAbandonedSelection } from '../utils'

type UseAbandonedSelectionsParams = {
  cguid: string
  appName: string
  appVersion: string
  signedIn: boolean
  productType: SearchProductList
}

export default function useAbandonedSelections({
  cguid,
  appName,
  appVersion,
  signedIn,
  productType,
}: UseAbandonedSelectionsParams) {
  const includeIsFavorite =
    useSeti({ experimentCode: 'HP_PUWYLO_FAVORITE', shouldFireImpression: false }) === 'VARIANT' && signedIn
  const includePackageSelections =
    useSeti({ experimentCode: 'WEB_HP_PACKAGE_SELECTIONS', shouldFireImpression: false }) === 'VARIANT'
  const resolvedProductType =
    !includePackageSelections && productType.includes('PKG')
      ? productType.filter((product) => product !== 'PKG')
      : productType

  const { data = null, error } = useQuery<AbandonedSelection | null>({
    queryKey: [cguid, appName, appVersion, resolvedProductType, includeIsFavorite, signedIn],
    queryFn: async () => {
      const selections = await fetchAbandonedSelections({
        cguid,
        appName,
        appVersion,
        productType: resolvedProductType,
        includeIsFavorite,
      })

      return getFirstValidAbandonedSelection(selections)
    },
    staleTime: Infinity,
    enabled: !signedIn,
  })

  if (error) {
    analytics.logError({
      message: 'Error thrown from useQuery',
      error: JSON.stringify(error),
      service: 'fetchAbandonedSelections',
    })
  }

  return data
}
